import {
  addressHelpers,
  type CountryCode,
  type ResolveAddressSuggestion,
  type SearchAddressSuggestions,
} from "@wayflyer/flyui";
import { useCallback, useRef } from "react";
import { useIntl } from "react-intl";

import { usePlacesApi } from "../../hooks/use-places-autocomplete/use-places-api";

import { MESSAGES } from "./message-definitions";

/**
 * Adapts the Google Places API calls to the search and resolve callbacks that
 * `useAddressAutocompleteFlow` expects. Thrown errors carry translated messages,
 * which the flow surfaces as its `error`.
 */
export const usePlacesAddressSuggestions = () => {
  const intl = useIntl();
  const { fetchAutocomplete, fetchPlaceDetails } = usePlacesApi();
  const mainTextById = useRef(new Map<string, string>());

  const searchAddressSuggestions = useCallback<SearchAddressSuggestions>(
    async ({ searchTerm, countryCode }) => {
      try {
        const response = await fetchAutocomplete(searchTerm, countryCode ? [countryCode as CountryCode] : undefined);
        const suggestions = addressHelpers.google.toAddressSuggestions(response);
        for (const suggestion of suggestions) {
          mainTextById.current.set(suggestion.id, suggestion.text);
        }
        return suggestions;
      } catch {
        throw new Error(intl.formatMessage(MESSAGES.suggestionsFailed));
      }
    },
    [fetchAutocomplete, intl]
  );

  const resolveAddressSuggestion = useCallback<ResolveAddressSuggestion>(
    async ({ id, countryCode }) => {
      const details = await fetchPlaceDetails(id);
      if (!details) {
        throw new Error(intl.formatMessage(MESSAGES.detailsFailed));
      }
      const parsedAddress = addressHelpers.google.parsePlaceDetails(details);
      // Google omits street_number/route for named places; the suggestion's main text is
      // the closest thing to a street line there.
      return addressHelpers.createDraftFromParsedAddress(
        { ...parsedAddress, addressLine1: parsedAddress.addressLine1 || mainTextById.current.get(id)?.trim() },
        countryCode
      );
    },
    [fetchPlaceDetails, intl]
  );

  return { searchAddressSuggestions, resolveAddressSuggestion };
};
